import {
  ASSIGNMENT_SOURCE_LABELS,
  BILLING_PERIODS,
  BILLING_PERIOD_LABELS,
  COMPANIES,
  COMPANY_LABELS,
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_CATEGORY_LABELS,
  EQUIPMENT_STATUS_LABELS,
  EQUIPMENT_STATUSES,
  SOFTWARE_CATEGORIES,
  SOFTWARE_CATEGORY_LABELS,
  USE_STATUS_LABELS,
  USE_STATUSES,
  formatDate,
  formatNumber,
  type UserDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Laptop, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel, PanelHeader } from '../../components/GlassPanel.js';
import { api, ApiError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { Field, Pill, SelectInput, TextInput } from './widgets.js';
import type { AssignmentDto, EquipmentDto, PlanDto, ProductDto, VendorDto } from './types.js';

const kr = (n: number) => `${formatNumber(n)} kr`;

type Section = 'software' | 'assignments' | 'equipment';

export function RegisterTab() {
  const [section, setSection] = useState<Section>('software');
  const tabs: { key: Section; label: string; Icon: typeof Boxes }[] = [
    { key: 'software', label: 'Programvare', Icon: Boxes },
    { key: 'assignments', label: 'Lisenstildelinger', Icon: ShieldCheck },
    { key: 'equipment', label: 'Utstyr', Icon: Laptop },
  ];

  return (
    <div className="space-y-4">
      <div className="border-hairline bg-panel/60 inline-flex rounded-lg border p-0.5">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setSection(key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-colors',
              section === key ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
            )}
          >
            <Icon size={14} aria-hidden />
            {label}
          </button>
        ))}
      </div>

      {section === 'software' && <SoftwareSection />}
      {section === 'assignments' && <AssignmentsSection />}
      {section === 'equipment' && <EquipmentSection />}
    </div>
  );
}

function useUsers() {
  return useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => api.get<UserDto[]>('/users?includeInactive=false'),
  });
}

function DeleteButton({ onClick, busy }: { onClick: () => void; busy?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label="Slett"
      className="text-ink-soft/60 hover:text-danger rounded p-1 transition disabled:opacity-40"
    >
      <Trash2 size={14} aria-hidden />
    </button>
  );
}

// ── Programvare: leverandører, produkter, planer ─────────────────────────────
function SoftwareSection() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['assets'] });
  };

  const vendors = useQuery({ queryKey: ['assets', 'vendors'], queryFn: () => api.get<VendorDto[]>('/assets/admin/vendors') });
  const products = useQuery({ queryKey: ['assets', 'products'], queryFn: () => api.get<ProductDto[]>('/assets/admin/products') });
  const plans = useQuery({ queryKey: ['assets', 'plans'], queryFn: () => api.get<PlanDto[]>('/assets/admin/plans') });
  const users = useUsers();

  const [newVendor, setNewVendor] = useState('');
  const addVendor = useMutation({
    mutationFn: () => api.post('/assets/admin/vendors', { name: newVendor.trim() }),
    onSuccess: () => {
      setNewVendor('');
      invalidate();
    },
  });
  const delVendor = useMutation({
    mutationFn: (id: string) => api.delete(`/assets/admin/vendors/${id}`),
    onSuccess: invalidate,
  });

  const [product, setProduct] = useState({ vendorId: '', name: '', category: 'PRODUCTIVITY', company: 'BEGGE' });
  const addProduct = useMutation({
    mutationFn: () => api.post('/assets/admin/products', product),
    onSuccess: () => {
      setProduct({ ...product, name: '' });
      invalidate();
    },
  });
  const delProduct = useMutation({
    mutationFn: (id: string) => api.delete(`/assets/admin/products/${id}`),
    onSuccess: invalidate,
  });

  const [plan, setPlan] = useState({ productId: '', name: '', pricePerSeat: '', currency: 'NOK', billingPeriod: 'MONTHLY', renewalDate: '', noticePeriodDays: '' });
  const addPlan = useMutation({
    mutationFn: () =>
      api.post('/assets/admin/plans', {
        productId: plan.productId,
        name: plan.name.trim(),
        pricePerSeat: Number(plan.pricePerSeat.replace(',', '.')),
        currency: plan.currency,
        billingPeriod: plan.billingPeriod,
        renewalDate: plan.renewalDate || null,
        noticePeriodDays: plan.noticePeriodDays ? Number(plan.noticePeriodDays) : null,
      }),
    onSuccess: () => {
      setPlan({ ...plan, name: '', pricePerSeat: '' });
      invalidate();
    },
  });
  const delPlan = useMutation({
    mutationFn: (id: string) => api.delete(`/assets/admin/plans/${id}`),
    onSuccess: invalidate,
  });

  const markMandatory = useMutation({
    mutationFn: (planId: string) =>
      api.post('/assets/admin/requirements', {
        planId,
        scope: 'ALL_EMPLOYEES',
        reason: 'Obligatorisk for alle ansatte.',
      }),
    onSuccess: invalidate,
  });

  const productName = (id: string) => products.data?.find((p) => p.id === id)?.name ?? '';

  return (
    <div className="space-y-5">
      {/* Vendors */}
      <GlassPanel>
        <PanelHeader title="Leverandører" subtitle="Hvem lisensen kjøpes fra." icon={<Boxes size={16} />} />
        <div className="mb-3 flex flex-wrap gap-2">
          <TextInput
            value={newVendor}
            onChange={(e) => setNewVendor(e.target.value)}
            placeholder="Ny leverandør (f.eks. Microsoft)"
            className="max-w-xs"
          />
          <Button size="sm" variant="primary" icon={<Plus size={13} />} disabled={!newVendor.trim()} loading={addVendor.isPending} onClick={() => addVendor.mutate()}>
            Legg til
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(vendors.data ?? []).map((v) => (
            <span key={v.id} className="border-hairline bg-base/40 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-xs">
              {v.name}
              <span className="text-ink-soft/60">{v.productCount}</span>
              <DeleteButton onClick={() => delVendor.mutate(v.id)} busy={delVendor.isPending} />
            </span>
          ))}
          {vendors.data?.length === 0 && <p className="text-ink-soft text-sm">Ingen leverandører ennå.</p>}
        </div>
      </GlassPanel>

      {/* Products */}
      <GlassPanel>
        <PanelHeader title="Produkter" subtitle="Programvare og abonnementer." />
        <div className="border-hairline bg-base/30 mb-4 grid gap-2 rounded-lg border p-3 sm:grid-cols-5">
          <Field label="Leverandør">
            <SelectInput value={product.vendorId} onChange={(e) => setProduct({ ...product, vendorId: e.target.value })}>
              <option value="">Velg …</option>
              {(vendors.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Navn">
            <TextInput value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} placeholder="ChatGPT" />
          </Field>
          <Field label="Kategori">
            <SelectInput value={product.category} onChange={(e) => setProduct({ ...product, category: e.target.value })}>
              {SOFTWARE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {SOFTWARE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Selskap">
            <SelectInput value={product.company} onChange={(e) => setProduct({ ...product, company: e.target.value })}>
              {COMPANIES.map((c) => (
                <option key={c} value={c}>
                  {COMPANY_LABELS[c]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <div className="flex items-end">
            <Button size="sm" variant="primary" icon={<Plus size={13} />} disabled={!product.vendorId || !product.name.trim()} loading={addProduct.isPending} onClick={() => addProduct.mutate()}>
              Legg til
            </Button>
          </div>
        </div>
        {addProduct.isError && <p className="text-danger mb-2 text-xs">{(addProduct.error as ApiError).message}</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-hairline text-ink-soft border-b text-left text-xs">
                <th className="py-2 pr-3 font-medium">Produkt</th>
                <th className="py-2 pr-3 font-medium">Leverandør</th>
                <th className="py-2 pr-3 font-medium">Kategori</th>
                <th className="py-2 pr-3 font-medium">Selskap</th>
                <th className="py-2 pr-3 font-medium">Planer</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {(products.data ?? []).map((p) => (
                <tr key={p.id} className="border-hairline/50 border-b last:border-0">
                  <td className="text-ink py-2 pr-3">{p.name}</td>
                  <td className="text-ink-soft py-2 pr-3">{p.vendorName}</td>
                  <td className="text-ink-soft py-2 pr-3">{SOFTWARE_CATEGORY_LABELS[p.category]}</td>
                  <td className="text-ink-soft py-2 pr-3">{COMPANY_LABELS[p.company]}</td>
                  <td className="text-ink-soft py-2 pr-3">{p.planCount}</td>
                  <td className="py-2 text-right">
                    <DeleteButton onClick={() => delProduct.mutate(p.id)} busy={delProduct.isPending} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.data?.length === 0 && <EmptyState title="Ingen produkter ennå." />}
        </div>
      </GlassPanel>

      {/* Plans */}
      <GlassPanel>
        <PanelHeader title="Planer og priser" subtitle="Nivå, pris, fornyelse og oppsigelsesfrist." />
        <div className="border-hairline bg-base/30 mb-4 grid gap-2 rounded-lg border p-3 sm:grid-cols-6">
          <Field label="Produkt" className="sm:col-span-2">
            <SelectInput value={plan.productId} onChange={(e) => setPlan({ ...plan, productId: e.target.value })}>
              <option value="">Velg …</option>
              {(products.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Plan">
            <TextInput value={plan.name} onChange={(e) => setPlan({ ...plan, name: e.target.value })} placeholder="Team" />
          </Field>
          <Field label="Pris/sete">
            <TextInput value={plan.pricePerSeat} onChange={(e) => setPlan({ ...plan, pricePerSeat: e.target.value })} placeholder="120" inputMode="decimal" />
          </Field>
          <Field label="Periode">
            <SelectInput value={plan.billingPeriod} onChange={(e) => setPlan({ ...plan, billingPeriod: e.target.value })}>
              {BILLING_PERIODS.map((b) => (
                <option key={b} value={b}>
                  {BILLING_PERIOD_LABELS[b]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Valuta">
            <SelectInput value={plan.currency} onChange={(e) => setPlan({ ...plan, currency: e.target.value })}>
              {['NOK', 'USD', 'EUR'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Fornyes">
            <TextInput type="date" value={plan.renewalDate} onChange={(e) => setPlan({ ...plan, renewalDate: e.target.value })} />
          </Field>
          <Field label="Oppsigelsesfrist (dager)">
            <TextInput value={plan.noticePeriodDays} onChange={(e) => setPlan({ ...plan, noticePeriodDays: e.target.value })} placeholder="30" inputMode="numeric" />
          </Field>
          <div className="flex items-end">
            <Button size="sm" variant="primary" icon={<Plus size={13} />} disabled={!plan.productId || !plan.name.trim() || !plan.pricePerSeat} loading={addPlan.isPending} onClick={() => addPlan.mutate()}>
              Legg til
            </Button>
          </div>
        </div>
        {addPlan.isError && <p className="text-danger mb-2 text-xs">{(addPlan.error as ApiError).message}</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="border-hairline text-ink-soft border-b text-left text-xs">
                <th className="py-2 pr-3 font-medium">Plan</th>
                <th className="py-2 pr-3 text-right font-medium">Pris/sete</th>
                <th className="py-2 pr-3 text-right font-medium">Årskostnad</th>
                <th className="py-2 pr-3 font-medium">Fornyes</th>
                <th className="py-2 pr-3 font-medium">Lisenser</th>
                <th className="py-2 pr-3 font-medium">Obligatorisk</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {(plans.data ?? []).map((p) => (
                <tr key={p.id} className="border-hairline/50 border-b last:border-0">
                  <td className="py-2 pr-3">
                    <span className="text-ink">{p.productName}</span>
                    <span className="text-ink-soft"> – {p.name}</span>
                  </td>
                  <td className="text-ink-soft py-2 pr-3 text-right tabular-nums">
                    {formatNumber(p.pricePerSeat)} {p.currency}
                  </td>
                  <td className="text-ink py-2 pr-3 text-right tabular-nums">{formatNumber(p.annualCostPerSeat)} {p.currency}</td>
                  <td className="text-ink-soft py-2 pr-3">{p.renewalDate ? formatDate(p.renewalDate) : '—'}</td>
                  <td className="text-ink-soft py-2 pr-3">{p.assignmentCount}</td>
                  <td className="py-2 pr-3">
                    {p.requirementCount > 0 ? (
                      <Pill tone="accent">Obligatorisk</Pill>
                    ) : (
                      <button type="button" onClick={() => markMandatory.mutate(p.id)} className="text-ink-soft/70 hover:text-accent text-[11px] underline">
                        Merk obligatorisk
                      </button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <DeleteButton onClick={() => delPlan.mutate(p.id)} busy={delPlan.isPending} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {plans.data?.length === 0 && <EmptyState title="Ingen planer ennå. Legg til en over." />}
        </div>
      </GlassPanel>
    </div>
  );
}

// ── Lisenstildelinger ────────────────────────────────────────────────────────
function AssignmentsSection() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['assets'] });
  const assignments = useQuery({ queryKey: ['assets', 'assignments'], queryFn: () => api.get<AssignmentDto[]>('/assets/admin/assignments') });
  const plans = useQuery({ queryKey: ['assets', 'plans'], queryFn: () => api.get<PlanDto[]>('/assets/admin/plans') });
  const users = useUsers();

  const [form, setForm] = useState({ planId: '', userId: '' });
  const add = useMutation({
    mutationFn: () => api.post('/assets/admin/assignments', { planId: form.planId, userId: form.userId || null }),
    onSuccess: () => {
      setForm({ ...form, userId: '' });
      invalidate();
    },
  });
  const [statusError, setStatusError] = useState<string | null>(null);
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/assets/admin/assignments/${id}`, { status }),
    onError: (e) => setStatusError((e as ApiError).message),
    onSuccess: () => {
      setStatusError(null);
      invalidate();
    },
  });
  const del = useMutation({ mutationFn: (id: string) => api.delete(`/assets/admin/assignments/${id}`), onSuccess: invalidate });

  return (
    <GlassPanel padded={false}>
      <div className="px-5 pt-5">
        <PanelHeader title="Lisenstildelinger" subtitle="Hvem har hvilken lisens — med kjøpt, fornyelse, oppsigelse, status og kostnad." icon={<ShieldCheck size={16} />} />
        <div className="border-hairline bg-base/30 mb-4 flex flex-wrap items-end gap-2 rounded-lg border p-3">
          <Field label="Plan">
            <SelectInput value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })} className="min-w-[14rem]">
              <option value="">Velg plan …</option>
              {(plans.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.productName} – {p.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Bruker">
            <SelectInput value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} className="min-w-[12rem]">
              <option value="">Uten bruker</option>
              {(users.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Button size="sm" variant="primary" icon={<Plus size={13} />} disabled={!form.planId} loading={add.isPending} onClick={() => add.mutate()}>
            Tildel
          </Button>
        </div>
        {statusError && <p className="text-danger mb-2 text-xs">{statusError}</p>}
      </div>
      <div className="overflow-x-auto px-5 pb-5">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="border-hairline text-ink-soft border-b text-left text-xs">
              <th className="py-2 pr-3 font-medium">Lisens</th>
              <th className="py-2 pr-3 font-medium">Bruker</th>
              <th className="py-2 pr-3 font-medium">Fornyes</th>
              <th className="py-2 pr-3 text-right font-medium">Årskostnad</th>
              <th className="py-2 pr-3 font-medium">Kilde</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(assignments.data ?? []).map((a) => (
              <tr key={a.id} className="border-hairline/50 border-b last:border-0">
                <td className="py-2 pr-3">
                  <span className="text-ink">{a.productName}</span>
                  <span className="text-ink-soft"> – {a.planName}</span>
                  {a.isMandatory && (
                    <span className="ml-1.5 inline-block align-middle">
                      <Pill tone="accent">Obligatorisk</Pill>
                    </span>
                  )}
                </td>
                <td className="text-ink-soft py-2 pr-3">{a.userName ?? a.externalId ?? <span className="italic">ikke matchet</span>}</td>
                <td className="text-ink-soft py-2 pr-3">{a.renewalDate ? formatDate(a.renewalDate) : '—'}</td>
                <td className="text-ink py-2 pr-3 text-right tabular-nums">{kr(a.annualCost)}</td>
                <td className="text-ink-soft py-2 pr-3 text-[11px]">{ASSIGNMENT_SOURCE_LABELS[a.source]}</td>
                <td className="py-2 pr-3">
                  <SelectInput
                    value={a.status}
                    onChange={(e) => setStatus.mutate({ id: a.id, status: e.target.value })}
                    className="h-8 w-36"
                    title={a.isMandatory ? 'Obligatorisk lisens kan ikke settes «ikke i bruk»' : undefined}
                  >
                    {USE_STATUSES.map((s) => (
                      <option key={s} value={s} disabled={s === 'NOT_IN_USE' && a.isMandatory}>
                        {USE_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </SelectInput>
                </td>
                <td className="py-2 text-right">
                  <DeleteButton onClick={() => del.mutate(a.id)} busy={del.isPending} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {assignments.data?.length === 0 && <EmptyState title="Ingen lisenstildelinger ennå." description="Legg til en over, eller importer en liste under «Import»." />}
      </div>
    </GlassPanel>
  );
}

// ── Utstyr ────────────────────────────────────────────────────────────────────
function EquipmentSection() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['assets'] });
  const equipment = useQuery({ queryKey: ['assets', 'equipment'], queryFn: () => api.get<EquipmentDto[]>('/assets/admin/equipment') });
  const users = useUsers();

  const [form, setForm] = useState({ userId: '', category: 'PC', model: '', receivedAt: '', purchasePrice: '', company: 'PROKOM', serialNumber: '' });
  const add = useMutation({
    mutationFn: () =>
      api.post('/assets/admin/equipment', {
        userId: form.userId || null,
        category: form.category,
        model: form.model.trim(),
        serialNumber: form.serialNumber.trim() || null,
        receivedAt: form.receivedAt,
        purchasePrice: form.purchasePrice ? Number(form.purchasePrice.replace(',', '.')) : null,
        company: form.company,
      }),
    onSuccess: () => {
      setForm({ ...form, model: '', serialNumber: '', purchasePrice: '' });
      invalidate();
    },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/assets/admin/equipment/${id}`, { status }),
    onSuccess: invalidate,
  });
  const del = useMutation({ mutationFn: (id: string) => api.delete(`/assets/admin/equipment/${id}`), onSuccess: invalidate });

  return (
    <GlassPanel padded={false}>
      <div className="px-5 pt-5">
        <PanelHeader title="Utstyr" subtitle="PC, mobil, skjerm m.m. — med type, modell, mottatt, kostnad og status." icon={<Laptop size={16} />} />
        <div className="border-hairline bg-base/30 mb-4 grid gap-2 rounded-lg border p-3 sm:grid-cols-4 lg:grid-cols-7">
          <Field label="Bruker">
            <SelectInput value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}>
              <option value="">Lager</option>
              {(users.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Type">
            <SelectInput value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {EQUIPMENT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {EQUIPMENT_CATEGORY_LABELS[c]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Modell" className="lg:col-span-2">
            <TextInput value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder={'MacBook Pro 14" M3'} />
          </Field>
          <Field label="Mottatt">
            <TextInput type="date" value={form.receivedAt} onChange={(e) => setForm({ ...form, receivedAt: e.target.value })} />
          </Field>
          <Field label="Pris">
            <TextInput value={form.purchasePrice} onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })} placeholder="24990" inputMode="decimal" />
          </Field>
          <Field label="Selskap">
            <SelectInput value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}>
              {COMPANIES.map((c) => (
                <option key={c} value={c}>
                  {COMPANY_LABELS[c]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <div className="flex items-end lg:col-span-7">
            <Button size="sm" variant="primary" icon={<Plus size={13} />} disabled={!form.model.trim() || !form.receivedAt} loading={add.isPending} onClick={() => add.mutate()}>
              Registrer utstyr
            </Button>
            {add.isError && <span className="text-danger ml-3 self-center text-xs">{(add.error as ApiError).message}</span>}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto px-5 pb-5">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-hairline text-ink-soft border-b text-left text-xs">
              <th className="py-2 pr-3 font-medium">Type</th>
              <th className="py-2 pr-3 font-medium">Modell</th>
              <th className="py-2 pr-3 font-medium">Bruker</th>
              <th className="py-2 pr-3 font-medium">Mottatt</th>
              <th className="py-2 pr-3 text-right font-medium">Pris</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(equipment.data ?? []).map((e) => (
              <tr key={e.id} className="border-hairline/50 border-b last:border-0">
                <td className="text-ink-soft py-2 pr-3">{EQUIPMENT_CATEGORY_LABELS[e.category]}</td>
                <td className="text-ink py-2 pr-3">{e.model}</td>
                <td className="text-ink-soft py-2 pr-3">{e.userName ?? <span className="italic">lager</span>}</td>
                <td className="text-ink-soft py-2 pr-3">{formatDate(e.receivedAt)}</td>
                <td className="text-ink-soft py-2 pr-3 text-right tabular-nums">{e.purchasePrice === null ? '—' : kr(e.purchasePrice)}</td>
                <td className="py-2 pr-3">
                  <SelectInput value={e.status} onChange={(ev) => setStatus.mutate({ id: e.id, status: ev.target.value })} className="h-8 w-36">
                    {EQUIPMENT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {EQUIPMENT_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </SelectInput>
                </td>
                <td className="py-2 text-right">
                  <DeleteButton onClick={() => del.mutate(e.id)} busy={del.isPending} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {equipment.data?.length === 0 && <EmptyState title="Ingen utstyr ennå." description="Registrer en enhet over, eller importer en liste under «Import»." />}
      </div>
    </GlassPanel>
  );
}
